/* eslint-disable jsdoc/require-param */
// React and external libraries
import React, { useEffect, useState } from "react";

// Material-UI components
import {
  DataGrid,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarExport,
} from "@material-ui/data-grid";

import { Button, Grid, Typography } from "@material-ui/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit } from "@fortawesome/free-solid-svg-icons";

import ManageDeal from "./ManageDeal";

import { supabase } from "../../../supabase";

// Components
import ModalCard from "../../shared/ModalCard";
import DataGridSearchBar from "../../shared/DataGridSearchBar";

/**
 *
 */
export default function DealsView(props) {
  // Hooks for context and state management
  const { supplierId } = props;
  const [loading, setLoading] = useState(false);
  const [deals, setDeals] = useState([]);
  const [displayDeals, setDisplayDeals] = useState([]);
  const [createDealOpen, setCreateDealOpen] = useState(false);

  // Filter States
  const [searchText, setSearchText] = useState("");

  // Modal states
  const [viewDeal, setViewDeal] = useState({
    isOpen: false,
    id: null,
  });

  // Modal handlers
  const handleViewDeal = deal => {
    setViewDeal({ isOpen: true, dealProp: deal.row });
  };

  // Function to filter leads based on search text.
  const requestSearch = searchValue => {
    // Set the search text state
    setSearchText(searchValue);

    // Create a regular expression to match the search value, ignoring case
    const searchRegex = new RegExp(escapeRegExp(searchValue), "i");

    // Filter the leads based on the search text
    const filteredRows = deals.filter(row => {
      // Check if any field in the row matches the search regex
      return Object.keys(row).some(field => {
        // Convert field value to string and test against search regex
        return searchRegex.test(row[field] && row[field].toString());
      });
    });

    // Update the displayed leads with the filtered rows
    setDisplayDeals(filteredRows);
  };

  const clearSearch = () => requestSearch("");

  // Helper function to escape special characters in a string
  const escapeRegExp = string => {
    return string.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&");
  };

  // Function to fetch leads data from Supabase based on applied filters and period.
  const fetchData = async (showLoading = true) => {
    // Set loading state to true
    if (showLoading) setLoading(true);

    // Create a Supabase query object
    let query = supabase
      .schema(process.env.REACT_APP_SB_SCHEMA)
      .from("deals_master")
      .select(
        "*, suppliers_master(supplier_name), refs_product_type(name), refs_deal_type(deal_type_name)"
      )
      .order("created_at", { ascending: false });

    // Apply supplier filter to the query if not 'all' suppliers selected
    query = query.eq("suppliers_master_id", supplierId);

    // Execute the query to fetch leads data from Supabase
    const { data: dealsItems, error } = await query;

    // Handle errors and update leads state with fetched data
    if (error) {
      console.error(error);
    } else {
      setDeals(dealsItems);
      setDisplayDeals(dealsItems);
    }

    // Set loading state to false
    if (showLoading) setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Effect to update displayed leads and perform search
  useEffect(() => {
    setDisplayDeals(deals);
    requestSearch(searchText);
  }, [deals]);

  // Components
  // Datagrid Toolbar - Function to render datagrid toolbar
  const quickSearchToolbar = () => {
    return (
      <GridToolbarContainer>
        <GridToolbarColumnsButton />
        <GridToolbarExport />
      </GridToolbarContainer>
    );
  };

  // Datagrid Columns - Define columns for the datagrid
  const columns = [
    {
      field: "View / Edit",

      width: 200,
      renderCell: params => {
        return (
          <Grid spacing={1} container direction="row">
            <Grid item xs={12}>
              <Button
                variant="text"
                color="primary"
                size="small"
                onClick={() => handleViewDeal(params)}
              >
                <FontAwesomeIcon icon={faEdit} />
              </Button>
            </Grid>
          </Grid>
        );
      },
    },
    { field: "deal_name", headerName: "Deal Name", width: 150 },
    {
      field: "deal_description",
      headerName: "Description",
      width: 300,
    },
    {
      field: "deal_type",
      headerName: "Deal Type",
      width: 150,
      renderCell: params => {
        return (
          <Typography>{params.row.refs_deal_type.deal_type_name}</Typography>
        );
      },
    },
    {
      field: "product_type",
      headerName: "Product Type",
      width: 200,
      renderCell: params => {
        return <Typography>{params.row.refs_product_type.name}</Typography>;
      },
    },
    {
      field: "supplier",
      headerName: "Supplier",
      width: 200,
      renderCell: params => {
        return (
          <Typography>{params.row.suppliers_master.supplier_name}</Typography>
        );
      },
    },
    { field: "created_at", headerName: "Created At", width: 150 },
  ];

  // MAIN RETURN - Render main components and modals
  return (
    <Grid spacing={3} container direction="column">
      <Grid item xs={12}>
        <Typography sx={{ mt: 4, mb: 2 }} variant="h4" component="div">
          Deals
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Button
          style={{ maxWidth: 180 }}
          variant="contained"
          color="primary"
          fullWidth
          onClick={() => {
            setCreateDealOpen(true);
          }}
        >
          Create Deal
        </Button>
      </Grid>
      <Grid item container justifyContent="space-between">
        <Grid
          item
          container
          rows
          spacing={1}
          justifyContent="space-between"
          alignItems="bottom"
        >
          <Grid
            item
            xs={12}
            md={2}
            alignContent="center"
            justifyContent="center"
          >
            <DataGridSearchBar
              requestSearch={requestSearch}
              searchText={searchText}
              clearSearch={clearSearch}
            />
          </Grid>
        </Grid>
      </Grid>

      <Grid item style={{ height: 500 }}>
        <DataGrid
          rows={displayDeals}
          columns={columns}
          loading={loading}
          components={{
            Toolbar: quickSearchToolbar,
          }}
        />
      </Grid>
      <ModalCard
        open={viewDeal.isOpen}
        onClose={() => {
          setViewDeal({ id: null, isOpen: false });
        }}
      >
        <ManageDeal
          supplierId={supplierId}
          editing
          dealProp={viewDeal.dealProp}
          onClose={() => {
            setViewDeal({ id: null, isOpen: false });
            fetchData();
          }}
        />
      </ModalCard>
      <ModalCard
        open={createDealOpen}
        onClose={() => {
          setViewDeal({ id: null, isOpen: false });
        }}
      >
        <ManageDeal
          supplierId={supplierId}
          onClose={() => {
            setCreateDealOpen(false);
            fetchData();
          }}
        />
      </ModalCard>
    </Grid>
  );
}
